import React, { useEffect, useRef } from 'react';
import katex from 'katex';

/**
 * QuestionPreview - Renders question content with LaTeX equations
 *
 * Shows a preview of the question and answers as they will appear to students
 */
export default function QuestionPreview({ questionType, questionText, answers, subjectName, image }) {
  const questionRef = useRef(null);
  const answersRef = useRef([]);

  // Function to render LaTeX in a container
  const renderLatex = container => {
    if (!container) return;

    const html = container.innerHTML;

    // Match $$...$$ patterns (display math)
    const rendered = html.replace(/\$\$(.*?)\$\$/g, (match, latex) => {
      try {
        return katex.renderToString(latex, {
          displayMode: false,
          throwOnError: false,
          output: 'html',
        });
      } catch (e) {
        console.error('KaTeX error:', e);
        return `<span class="katex-error">${latex}</span>`;
      }
    });

    container.innerHTML = rendered;
  };

  useEffect(() => {
    renderLatex(questionRef.current);
  }, [questionText]);

  useEffect(() => {
    answersRef.current.forEach(ref => renderLatex(ref));
  }, [answers]);

  const hasQuestion = questionText?.replace(/<[^>]*>/g, '').trim();

  // Get type label
  const getTypeLabel = () => {
    switch (questionType) {
      case 'MCQ':
        return 'Multiple Choice';
      case 'MultiSelect':
        return 'Multi-Select';
      case 'True/False':
        return 'True / False';
      case 'FIB':
        return 'Fill in the Blanks';
      case 'Matching':
        return 'Arrange in Order';
      case 'SwipeLeftRight':
        return 'Swipe Left / Right';
      case 'DragDrop':
        return 'Drag and Drop';
      case 'MatchingPairs':
        return 'Matching Pairs';
      default:
        return questionType || 'Question';
    }
  };

  // Get answer display based on type
  const renderAnswers = () => {
    if (!answers) return null;

    // True/False
    if (questionType === 'True/False') {
      return (
        <div className="preview-answers">
          <div className={`preview-answer-option ${answers === 'true' ? 'correct' : ''}`}>
            <span className="preview-option-marker">○</span>
            <span>True</span>
            {answers === 'true' && <span className="preview-correct-badge">✓</span>}
          </div>
          <div className={`preview-answer-option ${answers === 'false' ? 'correct' : ''}`}>
            <span className="preview-option-marker">○</span>
            <span>False</span>
            {answers === 'false' && <span className="preview-correct-badge">✓</span>}
          </div>
        </div>
      );
    }

    // MCQ, MultiSelect, FIB
    if (
      Array.isArray(answers) &&
      (questionType === 'MCQ' || questionType === 'MultiSelect' || questionType === 'FIB')
    ) {
      const validAnswers = answers.filter(a => a.answer?.trim());
      if (validAnswers.length === 0) return null;

      return (
        <div className="preview-answers">
          {validAnswers.map((answer, index) => (
            <div
              key={index}
              className={`preview-answer-option ${answer.is_correct ? 'correct' : ''}`}
            >
              <span className="preview-option-marker">
                {questionType === 'MultiSelect' ? '☐' : '○'}
              </span>
              <span
                ref={el => (answersRef.current[index] = el)}
                dangerouslySetInnerHTML={{ __html: answer.answer }}
              />
              {answer.is_correct && <span className="preview-correct-badge">✓</span>}
            </div>
          ))}
        </div>
      );
    }

    // Arrange in Order
    if (Array.isArray(answers) && questionType === 'Matching') {
      const validAnswers = answers.filter(a => a.ans?.trim());
      if (validAnswers.length === 0) return null;

      return (
        <div className="preview-answers preview-answers-ordered">
          <div className="preview-order-label">Correct order:</div>
          {validAnswers.map((answer, index) => (
            <div key={index} className="preview-answer-ordered">
              <span className="preview-order-number">{index + 1}</span>
              <span>{answer.ans}</span>
            </div>
          ))}
        </div>
      );
    }

    // Swipe Left/Right
    if (questionType === 'SwipeLeftRight' && answers) {
      return (
        <div className="preview-answers preview-swipe">
          <div className="preview-swipe-directions">
            <div
              className={`preview-swipe-option left ${answers.correctAnswer === 'left' ? 'correct' : ''}`}
            >
              <span className="swipe-arrow">←</span>
              <span>{answers.leftLabel || 'Left'}</span>
              {answers.correctAnswer === 'left' && <span className="preview-correct-badge">✓</span>}
            </div>
            <div
              className={`preview-swipe-option right ${answers.correctAnswer === 'right' ? 'correct' : ''}`}
            >
              <span>{answers.rightLabel || 'Right'}</span>
              <span className="swipe-arrow">→</span>
              {answers.correctAnswer === 'right' && (
                <span className="preview-correct-badge">✓</span>
              )}
            </div>
          </div>
          <div className="preview-swipe-hint">Student swipes card left or right</div>
        </div>
      );
    }

    // Drag and Drop (Matching/Categorization only)
    if (questionType === 'DragDrop' && answers && answers.mode === 'categorize') {
      const zones = answers.zones || [];
      const items = answers.items || [];

      return (
        <div className="preview-answers preview-categories">
          <div className="preview-order-label">Match items to categories:</div>
          {zones.map(zone => {
            const zoneItems = items.filter(it => it.zoneId === zone.id && it.text?.trim());
            return (
              <div key={zone.id} className="preview-category">
                <div className="preview-category-header">{zone.label}</div>
                <div className="preview-category-items">
                  {zoneItems.length > 0 ? (
                    zoneItems.map((item, idx) => (
                      <div key={idx} className="preview-category-item">
                        <span dangerouslySetInnerHTML={{ __html: item.text }} />
                      </div>
                    ))
                  ) : (
                    <div className="preview-category-empty">No items</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    // Matching Pairs (Terms ↔ Definitions)
    if (questionType === 'MatchingPairs' && Array.isArray(answers)) {
      const validPairs = answers.filter(p => p.term?.trim() || p.definition?.trim());
      if (validPairs.length === 0) return null;

      return (
        <div className="preview-answers preview-matching-pairs">
          <div className="preview-order-label">Match terms with definitions:</div>
          {validPairs.map((pair, index) => (
            <div key={index} className="preview-matching-pair">
              <span
                className="preview-pair-term"
                dangerouslySetInnerHTML={{ __html: pair.term || '(empty)' }}
              />
              <span className="preview-pair-arrow">↔</span>
              <span
                className="preview-pair-definition"
                dangerouslySetInnerHTML={{ __html: pair.definition || '(empty)' }}
              />
            </div>
          ))}
        </div>
      );
    }

    return null;
  };

  if (!questionType) {
    return (
      <div className="question-preview question-preview-empty">
        <div className="preview-placeholder">
          <span className="preview-icon">❓</span>
          <p>Select a question type to see preview...</p>
        </div>
      </div>
    );
  }

  if (!hasQuestion) {
    return (
      <div className="question-preview question-preview-empty">
        <div className="preview-placeholder">
          <span className="preview-icon">✏️</span>
          <p>Start typing your question to see preview...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="question-preview">
      <div className="preview-header">
        <span className="preview-icon">👁️</span>
        <span>Preview</span>
      </div>

      <div className="preview-content">
        {/* Question type badge */}
        <div className="preview-type-badge">{getTypeLabel()}</div>

        {/* Subject name if available */}
        {subjectName && <div className="preview-subject-name">{subjectName}</div>}

        {/* Question text */}
        <div className="preview-question-section">
          <div
            ref={questionRef}
            className="preview-question-text"
            dangerouslySetInnerHTML={{ __html: questionText }}
          />
        </div>

        {/* Attached image / diagram */}
        {typeof image === 'string' && image.startsWith('data:image') && (
          <div className="preview-image-section">
            <img className="preview-image" src={image} alt="Question attachment" />
          </div>
        )}

        {/* Answers */}
        {renderAnswers()}
      </div>
    </div>
  );
}
